import type { AgentMessageFragmentAction } from "@/hooks/use-stream-conversation";
import { useState, type FC } from "react";
import { Button } from "@/components/flexkit/Button";
import { IconError } from "@/assets/icons/geist/IconError";
import { IconSpinner } from "@/assets/icons/geist/IconSpinner";
import { IconCode } from "@/assets/icons/geist/IconCode";
import { cn } from "@/components/podkit/lib/cn";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/flexkit/Collapsible";
import { Text } from "@/components/podkit/typography/Text";
import { IconChevronRight } from "@/assets/icons/geist/IconChevronRight";
import { IconChevronDown } from "@/assets/icons/geist/IconChevronDown";
import { ScrollArea } from "@/components/flexkit/ScrollArea";

type FragmentActionProps = {
    action: AgentMessageFragmentAction;
};

export const FragmentAction: FC<FragmentActionProps> = ({ action }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const isExpandable = (action.state === "completed" || action.state === "failed") && action.resultDescription;
    return (
        <div
            className={cn("-mt-3 flex items-start gap-1 duration-700 animate-in fade-in first:mt-0", {
                // If the action is completed we want to show it instantly rather than fade it in.
                //
                // This ensures that we only fade in the action when it's being streamed in the first time,
                // and not if you're viewing the page later.
                "opacity-1 transition-none duration-0": action.state === "completed",
            })}
        >
            <Collapsible
                open={isExpanded}
                onOpenChange={setIsExpanded}
                disabled={!isExpandable}
                className="flex min-w-0 flex-grow flex-col"
            >
                <CollapsibleTrigger asChild>
                    <Button
                        variant="text"
                        size="md"
                        className="h-5 max-w-full self-start p-0 text-sm font-normal text-content-tertiary"
                        onClick={() => setIsExpanded(!isExpanded)}
                        data-testid={`action-${action.actionId}`}
                        data-tracking-id-none
                    >
                        {action.state === "completed" &&
                            (isExpanded ? (
                                <IconChevronDown size="sm" className="shrink-0 text-content-tertiary" />
                            ) : (
                                <IconChevronRight size="sm" className="shrink-0 text-content-tertiary" />
                            ))}
                        {action.state === "failed" && (
                            <IconError size="sm" className="shrink-0 text-content-tertiary" />
                        )}
                        {action.state === "running" && (
                            <IconSpinner size="sm" className="shrink-0 animate-spin text-content-orange" />
                        )}
                        {action.intentDescription}
                    </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="ml-5 mt-1 text-sm text-content-secondary">
                    <FragmentActionResult action={action} />
                </CollapsibleContent>
            </Collapsible>
        </div>
    );
};

const FragmentActionResult: FC<{ action: AgentMessageFragmentAction }> = ({ action }) => {
    return (
        <div className="flex flex-col overflow-hidden rounded-lg border border-border-base">
            <FragmentActionResultHeader action={action} />
            <ScrollArea orientation="both" className="flex max-h-[300px] pl-[3px]">
                <pre className="p-2 font-mono text-sm text-content-secondary">
                    <code>{action.resultDescription}</code>
                </pre>
            </ScrollArea>
        </div>
    );
};

const FragmentActionResultHeader: FC<{
    action: AgentMessageFragmentAction;
}> = ({ action }) => {
    return (
        <div className="flex items-center gap-2 border-b border-border-base bg-surface-02 p-2">
            <IconCode size="base" className="shrink-0 text-content-secondary" />
            <Text className="min-w-0 break-words text-sm font-bold text-content-secondary">
                {action.intentDescription}
            </Text>
        </div>
    );
};
